<template>
    <!-- Shared pig-tally chart (pig balance + deaths on a two-pane stock chart, the current Production Group shown
         by default with 12 months of history reachable through the navigator, explicit day/week
         granularity, rewind-to-default button). Used by both MortalityDetailModal (its primary
         chart) and WaterDetailModal (ported alongside the water-norm chart, since the water index
         moves with the herd). -->
    <div class="rounded-lg bg-white p-4 shadow-sm">
        <div class="mb-2 flex items-center gap-2">
            <h3 class="flex-1 truncate text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400">
                {{ $t('views.monitoring.activeGroups.modals.mortalityDetail.chartTitle') }}
            </h3>
            <!-- A negative tally always means missing or incorrect movement registrations. -->
            <span
                v-if="hasNegativeTally"
                class="cursor-help"
                :title="$t('views.monitoring.activeGroups.modals.mortalityDetail.chartNegativeWarning')"
            >
                <AlertTriangle class="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden="true" />
            </span>
            <div class="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
                <button
                    v-for="option in granularityOptions"
                    :key="option"
                    type="button"
                    class="rounded-md px-2 py-0.5 text-xs font-medium transition-colors"
                    :class="granularity === option ? 'bg-primary-700 text-white' : 'text-gray-600 hover:bg-gray-100'"
                    @click="granularity = option"
                >
                    {{ $t(`views.monitoring.activeGroups.texts.${option}`) }}
                </button>
            </div>
            <button
                type="button"
                class="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                :title="$t('views.monitoring.activeGroups.modals.mortalityDetail.chartReset')"
                @click="resetChartView"
            >
                <RotateCcw class="h-3.5 w-3.5" aria-hidden="true" />
            </button>
        </div>
        <div v-if="isLoading" class="flex items-center justify-center py-16">
            <Spinner class="h-6 w-6" />
        </div>
        <!-- destroy-on-update: dataGrouping changes don't survive chart.update(), so the
             granularity toggle recreates the chart instead. -->
        <GenericChart
            v-else-if="tallySeries.length"
            :options="(chartOptions as any)"
            :expandable="false"
            :destroy-on-update="true"
            @ready="onChartReady"
            @update="onChartReady"
        />
        <p v-else class="py-6 text-center text-xs italic text-gray-400">
            {{ $t('views.monitoring.activeGroups.modals.mortalityDetail.chartEmpty') }}
        </p>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { AlertTriangle, RotateCcw } from 'lucide-vue-next';
import merge from 'lodash-es/merge';
import GenericChart from '@/components/charts/GenericChart.vue';
import useDefaultChartOptions from '@/components/charts/composables/useDefaultChartOptions';
import Spinner from '@/components/ui/Spinner.vue';
import { i18n } from '@/plugins/internationalization/i18n';
import { usePigTallySeries } from '@/plugins/store/actions/queries/active-groups';
import { ChartType } from '@/types';
import { useChartGranularity } from '@/composables/useChartGranularity';
import { directoryLocalDayStart } from './directoryLocalNow';
import { computeFlagBands } from './flagBands';
import { formatActiveGroupsChartTooltipHeader, activeGroupsWeekAxisLabels } from './activeGroupsChartFormatting';

const props = defineProps<{
    // The section whose pig tally is charted (null while the parent modal is closed - the fetch
    // stays disabled until this is set).
    sectionId: number | null;
    // The active Production Group's day count (null when there's no active Production Group) - the chart
    // opens showing the current Production Group (its start → today); the navigator reaches the
    // rest of the 12-months series.
    day: number | null;
    // Now on the Directory's own wall clock, off the live-KPI payload (see directoryLocalNow.ts).
    // Both edges of this axis are measured from it: a browser-computed "today" lands a whole UTC
    // offset away from the daily buckets drawn against it. Null only before the first read lands.
    directoryLocalNow: string | null;
}>();

const sectionId = computed(() => props.sectionId);
const { data: tallyData, isLoading } = usePigTallySeries(sectionId);
const tallySeries = computed(() => tallyData.value ?? []);

// Explicit granularity toggle: raw daily points, or Monday→Sunday weekly buckets where deaths
// sum (a weekly death toll) and the pig balance averages.
type TallyGranularity = 'day' | 'week';
const { granularityOptions, granularity, resetGranularity } = useChartGranularity<TallyGranularity>(['day', 'week'], sectionId, 'day');

const weeklyGrouping = { forced: true, units: [['week', [1]]] as [string, number[]][] };

// A negative pig balance can't happen physically — it flags missing or incorrect movement
// registrations, surfaced as a warning icon next to the chart title.
const hasNegativeTally = computed(() => tallySeries.value.some((point) => point.pigs < 0));

// The below-zero stretches as faint amber bands across both chart panes (same signal as the
// warning glyph). Bands are date-range based, so they stay visible even when weekly averaging
// smooths the dip out of the line itself.
const negativeBands = computed(() => computeFlagBands(tallySeries.value, (point) => Date.parse(point.date), (point) => point.pigs < 0));

// The chart opens showing the current Production Group (its start → today); the navigator
// reaches the rest of the 12-months series.
const defaultRangeStart = computed(() => {
    if (props.day === null) return null;
    return directoryLocalDayStart(props.directoryLocalNow) - props.day * 24 * 60 * 60 * 1000;
});

// The rendered chart instance (captured on ready) — lets the reset button restore the extremes.
const chart = ref<any>(null);

function onChartReady(instance: unknown): void {
    chart.value = instance;
}

// Back to the default view: current Production Group range, daily granularity.
function resetChartView(): void {
    resetGranularity();
    chart.value?.xAxis?.[0]?.setExtremes(defaultRangeStart.value ?? undefined, undefined);
}

// App-wide chart conventions (localized week-number axis, fonts, series palette) come from the
// shared wrapper; the custom-tooltip flag skips its per-point tooltip so the shared one below
// can show both panes' values in a single box.
const stockDefaults = useDefaultChartOptions({}, { type: ChartType.Stock }, true);

const chartOptions = computed(() => {
    const base: Record<string, unknown> = merge({} as Record<string, unknown>, stockDefaults.value);
    // The wrapper's single-axis defaults and period-picker range don't apply to the two-pane
    // layout below, which replaces the axis, and min/max follow the Production Group instead.
    delete base.yAxis;

    return merge(base, {
        chart: {
            height: 300,
            spacingLeft: 0,
            spacingRight: 0,
            backgroundColor: 'transparent',
            marginRight: 8,
            zoomType: null,
        },
        exporting: { enabled: false },
        rangeSelector: { enabled: false },
        scrollbar: { enabled: false },
        navigator: { enabled: true, height: 30, margin: 8 },
        legend: { enabled: true, itemStyle: { color: '#6B7280', fontSize: '11px', fontWeight: '500' } },
        xAxis: {
            min: defaultRangeStart.value,
            // The present moment, not the last datapoint - a Sensor that stopped reporting
            // days ago must render as empty space up to the right edge, not as a shortened axis.
            max: directoryLocalDayStart(props.directoryLocalNow),
            tickInterval: null,
            startOfWeek: 1,
            labels: activeGroupsWeekAxisLabels,
            // Marks where the current Production Group started, so a pan into history via the navigator
            // keeps the Production Group boundary visible (in the default view it sits at the left edge).
            plotLines:
                defaultRangeStart.value !== null
                    ? [
                          {
                              value: defaultRangeStart.value,
                              color: '#9CA3AF',
                              dashStyle: 'Dash',
                              width: 1,
                              zIndex: 3,
                              label: {
                                  text: i18n.global.t('views.monitoring.activeGroups.modals.mortalityDetail.chartSpStart') as string,
                                  rotation: 0,
                                  y: 12,
                                  style: { fontSize: '10px', color: '#9CA3AF' },
                              },
                          },
                      ]
                    : [],
            plotBands: negativeBands.value,
        },
        // Volume-pane layout: pig balance on top, deaths in their own pane with their own scale
        // so single-death days stay visible next to a four-digit pig count. softMin keeps the
        // zero baseline but lets the axis flex below it when a tally goes negative.
        yAxis: [
            { height: '64%', softMin: 0, allowDecimals: false, opposite: true, gridLineDashStyle: 'Dash', title: { text: '' }, labels: { style: { fontSize: '0.75rem' } } },
            { top: '70%', height: '30%', offset: 0, softMin: 0, allowDecimals: false, opposite: true, gridLineDashStyle: 'Dash', title: { text: '' }, labels: { style: { fontSize: '0.75rem' } } },
        ],
        tooltip: {
            shared: true,
            split: false,
            formatter(this: any): string {
                const header = `<span style="font-size: 10px">${formatActiveGroupsChartTooltipHeader(this.x, this.points?.[0]?.series?.chart?.time)}</span>`;
                return (this.points ?? []).reduce(
                    (acc: string, point: any) =>
                        `${acc}<br/><span style="color:${point.color}">●</span> ${point.series.name}: <b>${i18n.global.n(Math.round(point.y))}</b>`,
                    header
                );
            },
        },
        series: [
            {
                // Weekly buckets read as discrete totals, so the pig balance switches to columns there.
                type: granularity.value === 'week' ? 'column' : 'line',
                name: i18n.global.t('views.monitoring.activeGroups.modals.mortalityDetail.chartPigs') as string,
                data: tallySeries.value.map((point) => [Date.parse(point.date), point.pigs]),
                yAxis: 0,
                marker: { enabled: false },
                borderRadius: 1,
                // Below-zero values paint in the same amber as the header warning glyph: a
                // negative tally is a registration problem, not a mortality signal.
                negativeColor: '#F59E0B',
                dataGrouping: granularity.value === 'week' ? { ...weeklyGrouping, approximation: 'average' } : { enabled: false },
            },
            {
                type: 'column',
                name: i18n.global.t('views.monitoring.activeGroups.modals.mortalityDetail.chartDead') as string,
                data: tallySeries.value.map((point) => [Date.parse(point.date), point.dead]),
                yAxis: 1,
                minPointLength: 2,
                borderRadius: 1,
                dataGrouping: granularity.value === 'week' ? { ...weeklyGrouping, approximation: 'sum' } : { enabled: false },
            },
        ],
    });
});
</script>
